import { t, intl } from 'translations/i18n'

import ErrorMessageController from '../../controllers/document_upload/error_message_controller'

export interface Delegate {
  // targets
  fileInputTarget: HTMLInputElement
  formTarget: HTMLFormElement
  fileAttachmentItemTemplateTarget: HTMLTemplateElement
  fileAttachmentListTemplateTarget: HTMLTemplateElement
  uploadFilesContainerTarget: HTMLInputElement
  showFilesUploadContainerButtonTarget: HTMLInputElement
  hideFilesUploadContainerButtonTarget: HTMLInputElement
  // values
  errorMessage: ErrorMessageController
  maxFileSizeBytesValue: number
  allowedFileTypesValue: string[]
}

export class FileAttachmentService {
  delegate: Delegate
  files: File[]

  constructor(delegate: Delegate) {
    this.delegate = delegate
    this.files = []
  }

  onFileChanged(e: InputEvent) {
    if (!this.delegate.fileInputTarget.files) return

    const fileInputElement = e.target as HTMLInputElement
    const fileList = fileInputElement.files || []
    const filesArray = Array.from(fileList)
    const isValid = this.validateFiles(filesArray)
    // Reset the file input value to allow re-uploading the same file
    fileInputElement.value = ''

    if (!isValid) return

    this.setFiles([...this.files, ...filesArray])
  }

  validateFiles(files: File[]): boolean {
    this.delegate.errorMessage?.clear()

    const errors = files.reduce((errors, file) => {
      if (file.size > this.delegate.maxFileSizeBytesValue) {
        const size = this.bytesToHumanReadable(this.delegate.maxFileSizeBytesValue)
        errors.add(t('document_upload.file_size_error', { size }))
      }

      if (!this.isAllowedFileType(file.type)) {
        errors.add(t('document_upload.file_type_error'))
      }

      return errors
    }, new Set<string>())
    this.showErrorMessage(Array.from(errors))
    const isValid = errors.size === 0

    return isValid
  }

  showErrorMessage(errors: string[]) {
    if (errors.length === 0) return

    const title = t('document_upload.file_error_title')
    const liElements = errors.map((e) => {
      const liElement = document.createElement('li')
      liElement.textContent = e

      return liElement
    })
    const ulElement = document.createElement('ul')
    ulElement.classList.add('list-disc', 'pl-4')
    liElements.forEach((el) => ulElement.appendChild(el))

    this.delegate.formTarget.querySelector('#file-attachment-list')?.remove()
    this.delegate.errorMessage.set({ title, messageElement: ulElement })
  }

  setFiles(files: File[]) {
    this.files = files
    this.renderFileAttachmentListElement()
  }

  renderFileAttachmentListElement() {
    this.delegate.formTarget.querySelector('#file-attachment-list')?.remove()
    const list =
      this.delegate.fileAttachmentListTemplateTarget.content.firstElementChild?.cloneNode(
        true,
      ) as HTMLElement
    if (!list) return

    this.files.forEach(this.renderFileAttachmentItemElement(list))
    this.delegate.formTarget.prepend(list)
  }

  renderFileAttachmentItemElement = (list: HTMLElement) => (file: File) => {
    const item =
      this.delegate.fileAttachmentItemTemplateTarget.content.firstElementChild?.cloneNode(
        true,
      ) as HTMLElement
    if (!item) return

    const fileNameElement = item.querySelector('#file-name')
    if (fileNameElement) fileNameElement.innerHTML = file.name

    const fileSizeElement = item.querySelector('#file-size')
    if (fileSizeElement) fileSizeElement.innerHTML = this.bytesToHumanReadable(file.size)

    const removeBtnElement = item.querySelector('#remove-btn')
    if (removeBtnElement) removeBtnElement.addEventListener('click', this.removeFile(file))

    list.appendChild(item)
  }

  removeFile = (file: File) => (e: Event) => {
    e.stopPropagation()
    e.preventDefault()

    const filteredFiles = this.files.filter((f) => f !== file)
    this.setFiles(filteredFiles)
  }

  bytesToHumanReadable(size: number) {
    const units = ['bytes', 'KB', 'MB', 'GB']
    let unit = 0
    let sizeToTransform = size

    while (sizeToTransform >= 1024 && ++unit) {
      sizeToTransform = sizeToTransform / 1024
    }

    const localizedNumber = sizeToTransform.toLocaleString(intl.locale, {
      maximumFractionDigits: 1,
    })

    return localizedNumber + ' ' + units[unit]
  }

  isAllowedFileType(type: string) {
    return this.delegate.allowedFileTypesValue.some((allowedType) => type.startsWith(allowedType))
  }

  showFilesUploadContainer() {
    this.delegate.uploadFilesContainerTarget.classList.remove('hidden')
    this.delegate.hideFilesUploadContainerButtonTarget.classList.remove('hidden')
    this.delegate.showFilesUploadContainerButtonTarget.classList.add('hidden')
  }

  hideFilesUploadContainer() {
    this.delegate.uploadFilesContainerTarget.classList.add('hidden')
    this.delegate.hideFilesUploadContainerButtonTarget.classList.add('hidden')
    this.delegate.showFilesUploadContainerButtonTarget.classList.remove('hidden')
  }
}
